import React from 'react';

import type { CheckoutProvider } from '@/utils/session-invoice';
import type {
  SpeiCheckoutAccounting,
  StripeCheckoutAccounting,
} from '@/utils/session-checkout-accounting';

export type TextTranslations = Record<string, string>;

// Component
export interface ConfiguratorProperties {
  dsExternalType: string;
  dsType: string;
  hasTargetId: boolean;
  pathParamKey: string;
  targetId: string;
  targetIdFromPath: boolean;
}

export interface ConfiguratorProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: ConfiguratorProperties;
}

// API Response
// /configurator
export interface EntryDate {
  created_at: string;
  updated_at: string;
}

export interface EntryTimeDuration {
  start_time: string;
  end_time: string;
}

export interface EntryDateDuration {
  start_date: string;
  end_date: string;
}

export interface EntryMeta {
  description: string;
  is_active: boolean;
  art_bundle: ArtBundle[];
}

export interface ArtBundleProps {
  name?: TextTranslations;
  title?: TextTranslations;
  description?: TextTranslations;
  benefits?: TextTranslations;
  page_configurator_preview?: string;
  section_id?: string;
  src?: string;
  isSelected?: boolean;
  showPrice?: boolean;
  comingSoon?: boolean;
  /** Operating-region code (e.g. "MEX", "INT") for a per-region market-benchmark bundle. */
  country_code?: string;
  /** Position of the "Market" indicator on the You-vs-Market bar (0-100) for `country_code`.
   *  /configurator sends it as a string (e.g. "60"). */
  feature_level?: string | number;
  /** Per-tier competitiveness scores (0-100) on the tier's score art bundle; sent as
   *  strings. Used to derive a feature's "unlock" benefit deltas (top tier minus current). */
  score_market?: string | number;
  score_profitability?: string | number;
  score_stickiness?: string | number;
  /** Per-highlight demo-clip URL (on a `section_id: 'highlight'` art bundle). Plays in
   *  the centre stage while that highlight is active (configurator features step + the
   *  product feature-detail showcase). */
  video_url?: string;
  /** Demo-clip length in seconds (`section_id: 'highlight'` art bundle); sent as a string.
   *  Caps how long the highlight stays active during the showcase autoplay. */
  video_duration_s?: string | number;
}

export interface ArtBundle {
  logo?: string;
  text_name?: TextTranslations;
  properties?: ArtBundleProps;
}

export interface Currency extends EntryDate, EntryMeta {
  code: string;
  priority: number;
}

export interface Language extends EntryDate, EntryMeta {
  code: string;
  priority: number;
}

export interface Discount extends EntryTimeDuration {
  active: boolean;
  duration: string;
}

export interface BundleData {
  label: string;
}

export type CurrencyCost = Record<string, number>;

export interface Cost {
  cost: CurrencyCost;
  gamification_tier_id: number;
}

export interface BundleItem {
  discounted_cost: Cost;
  gamification_item_id: number;
}

export interface GamificationBundle extends EntryDate, EntryTimeDuration, EntryMeta {
  id: number;
  name: string;
  priority: number;
  discount: Discount;
  bundle_items: BundleItem[];
  bundle_data: BundleData;
}

export interface ClassData {
  active_scope: string[];
}

export interface GamificationClass extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  class_data: ClassData;
  info_points_art_bundle?: Record<string, string>;
}

export interface ItemData {
  gamification_class_id: number;
}

export interface DiscountedCost extends EntryDateDuration {
  costs: Cost[];
  active: boolean;
}

export interface GamificationItem extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  item_data: ItemData;
  cost: Cost[];
  discounted_cost?: DiscountedCost;
}

export interface TenantData {
  domain: string;
  site_id: number;
  tenant_id: number;
}

export interface GamificationTier extends EntryDate, EntryMeta {
  gamification_item_id: number;
  id: number;
  name: string;
  priority: number;
  tenant_data: TenantData;
}

export interface MapsServiceIdentifier {
  operating_region_ids: string[];
}

export interface OperatingRegionGroup extends EntryDate, EntryMeta {
  id: number;
  name: string;
  language: string;
  priority: number;
  maps_service_identifier: MapsServiceIdentifier;
}

export interface StatsInfo {
  area_sq_km: number;
  population: number;
}

export interface OperatingRegionCurrency extends EntryMeta {
  currency_code: string;
  format: string;
  class_type: string;
  metadata?: Record<string, string>;
}

export interface OperatingRegionData {
  /** Language codes available for this region; index 0 is the market's primary language. */
  language_codes?: string[];
  /** Payment methods OWS offers for this region ('spei' | 'card'). NEW Celestial field,
   *  exact name TBD with backend; absent until backend ships it → code-map fallback. */
  owner_hub_payment_methods?: string[];
  currency_code?: string;
  tax_code?: string;
  timezone?: string;
  destination_cluster?: string;
  enable_content_management_task?: boolean;
}

export interface OperatingRegion extends EntryDate, EntryMeta {
  code: string;
  name: string;
  priority: number;
  stats_info?: StatsInfo;
  currency_code?: string;
  currency?: OperatingRegionCurrency;
  region_data?: OperatingRegionData;
}

export interface ArtStyleData {
  site_decoration_id: number;
  site_visual_style_id: number;
  site_font_icon_style_id: number;
  site_iconography_style_id: number;
  compatibility: { site_seed_id: number; site_skin_id: number }[];
}

export interface VisualStyleDataAttributes extends Record<string, string> {
  site_art_style_banner_set_id: string;
}

export interface VisualStyleData {
  attributes: VisualStyleDataAttributes;
}

export interface VisualStyle extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  visual_style_data: VisualStyleData;
}

export interface DecorationDataAttributes extends Record<string, string> {
  site_art_style_decorations_set_id: string;
}

export interface DecorationData {
  attributes: DecorationDataAttributes;
}

export interface Decoration extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  decoration_data: DecorationData;
}

export interface IconographyStyleDataAttributes extends Record<string, string> {
  site_art_style_iconography_set_id: string;
}

export interface IconographyStyleData {
  attributes: IconographyStyleDataAttributes;
}

export interface IconographyStyle extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  iconography_style_data: IconographyStyleData;
}

interface FontIconStyleData {
  css_url: string;
}

export interface FontIconStyle extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  font_icon_style_data: FontIconStyleData;
}

export interface SiteArtStyleAttributes extends Record<string, string> {
  site_art_style_banner_set_id: string;
  site_art_style_decorations_set_id: string;
  site_art_style_iconography_set_id: string;
}

export interface SiteArtStyle extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  art_style_data: ArtStyleData;
  visual_style: VisualStyle;
  decoration: Decoration;
  iconography_style: IconographyStyle;
  font_icon_style: FontIconStyle;
  attributes: SiteArtStyleAttributes;
}

export interface LogoPresentation {
  aspect_ratio: string;
  logo_presentation_aspect_ratio: string;
}

export interface SeedData {
  design_variant_id: string;
  page_id: string;
  preview_url: string;
}

export interface SiteSeed extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  logo_presentation: LogoPresentation;
  seed_data: SeedData;
}

export interface GroupData {
  site_seed_ids: number[];
  site_skin_ids: number[];
}

export interface SiteSeedGroup extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  group_data: GroupData;
  site_seed: SiteSeed;
  site_skins: SiteSkin[];
}

export interface SkinData {
  design_theme_id: string;
}

export type Tokens = Record<string, string>;

export interface SiteSkin extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  skin_data: SkinData;
  tokens: Tokens;
}

// API - Traffic Options
export interface TrafficOptionHighlightItem {
  name: TextTranslations;
  logo: string;
}

export interface TrafficOptionHighlightGroup {
  title: TextTranslations;
  items: TrafficOptionHighlightItem[];
  more_count?: number;
  more_label?: TextTranslations;
}

export interface TrafficOptionHighlight {
  title: TextTranslations;
  description: TextTranslations;
  groups: TrafficOptionHighlightGroup[];
}

/** Per-operating-region content for a traffic option. */
export interface TrafficOptionRegion {
  name?: TextTranslations;
  image?: string;
  video?: string;
  highlights?: TrafficOptionHighlight[];
}

export interface TrafficOptionData {
  id?: string;
  key?: string;
  name?: TextTranslations;
  image?: string;
  video?: string;
  is_active?: boolean;
  art_bundle?: ArtBundle[];
  description?: TextTranslations;
  /**
   * Per-operating-region content keyed by OperatingRegion.code (e.g. "MEX", "INT").
   * An option is offered in a region iff `regions[code]` exists; that entry carries the
   * region-specific name/media/highlights (different sources/pills/text per country).
   */
  regions?: Record<string, TrafficOptionRegion>;
}

export interface TrafficOption extends EntryDate, EntryMeta {
  id: number;
  name: string;
  priority: number;
  traffic_option_data: TrafficOptionData;
}

export interface ConfiguratorConfigRoot {
  defaultCountryCode: string;
  languages: Language[];
  currencies: Currency[];
  gamification_bundles: GamificationBundle[];
  gamification_classes: GamificationClass[];
  gamification_items: GamificationItem[];
  gamification_tiers: GamificationTier[];
  operating_region_groups: OperatingRegionGroup[];
  operating_regions: OperatingRegion[];
  site_art_styles: SiteArtStyle[];
  site_seed_groups: SiteSeedGroup[];
  traffic_options: TrafficOption[];
}

// /session/price
export interface PricingCosts {
  currency: string;
  totalCost: number;
  discount?: TextTranslations;
  cost: number;
}

export interface PriceMap {
  price: PricingCosts;
  baseItemsPrice: PricingCosts;
  itemsPrice: PricingCosts;
}

export interface EngagementOffer {
  /** Discounted price if a promotion applies, otherwise same as `originalPrice`. */
  price: number;
  /** Base price — present only when a discount exists. */
  originalPrice?: number;
  currency: string;
  /** True when the user has changed at least one tier from the campaign defaults. */
  isCustom: boolean;
}

// API Request
export interface DesignVariantRequestParameters extends Record<string, string | undefined> {
  // design.designVariant
  wsid?: string;
  // design.designPage
  wsp?: string;
  // color.colorsId
  wsc?: string;
  // color.themesId
  wst?: string;
  // attributes.site_art_style_banner_set_id
  site_art_style_banner_set_id?: string;
  // attributes.site_art_style_decorations_set_id
  site_art_style_decorations_set_id?: string;
  // attributes.site_art_style_iconography_set_id
  site_art_style_iconography_set_id?: string;
}

// UI - Countries
export interface Country extends OperatingRegion {}

export interface CountryGroup extends OperatingRegionGroup {
  regions: Country[];
}

export interface ConfiguratorContextCountry extends Country {
  active: boolean;
  onClick: ConfiguratorCountryClickHandler;
}

export type ConfiguratorCountryClickHandler = (nextCountry: Country) => void;

export interface ConfiguratorContextCountryGroup extends CountryGroup {
  regions: ConfiguratorContextCountry[];
}

// UI - Steps
export interface ConfiguratorStep {
  id: string;
  name: TextTranslations;
}

export interface ConfiguratorContextStep extends ConfiguratorStep {
  active: boolean;
  onClick: ConfiguratorStepClickHandler;
  isValid: boolean;
}

export type ConfiguratorStepClickHandler = (nextStep: ConfiguratorStep) => void;

// UI - Design Categories
export interface ConfiguratorDesignCategory {
  id: string;
  name: TextTranslations;
}

export interface ConfiguratorContextDesignCategory extends ConfiguratorDesignCategory {
  active: boolean;
  expanded: boolean;
  onClick: ConfiguratorDesignCategoryClickHandler;
}

export type ConfiguratorDesignCategoryClickHandler = (nextCategory: ConfiguratorDesignCategory) => void;

// UI - Design - Theme Options
export interface ConfiguratorDesignThemeOption extends SiteSeedGroup {}

export interface ConfiguratorContextDesignThemeOption extends ConfiguratorDesignThemeOption {
  active: boolean;
  onClick: ConfiguratorDesignThemeClickHandler;
}

export type ConfiguratorDesignThemeClickHandler = (
  nextThemeOption: ConfiguratorDesignThemeOption,
  artOption: ConfiguratorDesignArtOption | undefined,
) => void;

// UI - Design - Colors Options
export interface ConfiguratorDesignColorsOption extends SiteSkin {}

export interface ConfiguratorContextDesignColorsOption extends ConfiguratorDesignColorsOption {
  active: boolean;
  onClick: ConfiguratorDesignColorsClickHandler;
}

export type ConfiguratorDesignColorsClickHandler = (
  nextColorsOption: ConfiguratorDesignColorsOption,
  themeOption: ConfiguratorDesignThemeOption | undefined,
  artOption: ConfiguratorDesignArtOption | undefined,
) => void;

// UI - Design - Art Style Options
export interface ConfiguratorDesignArtOption extends SiteArtStyle {}

export interface ConfiguratorContextDesignArtOption extends ConfiguratorDesignArtOption {
  active: boolean;
  onClick: ConfiguratorDesignArtClickHandler;
}

export type ConfiguratorDesignArtClickHandler = (nextArtOption: ConfiguratorDesignArtOption) => void;

// UI - Engagement
export interface ConfiguratorEngagementBundle extends GamificationBundle {}

export type ConfiguratorEngagementBundlesMap = Record<string, ConfiguratorEngagementBundle>;

export interface ConfiguratorContextEngagementBundle extends ConfiguratorEngagementBundle {
  active: boolean;
  onClick: ConfiguratorEngagementBundleClickHandler;
  price: PricingCosts;
}

export type ConfiguratorEngagementBundleClickHandler = (nextBundle: ConfiguratorEngagementBundle) => void;

export interface ConfiguratorEngagementItem extends GamificationItem {}

export type ConfiguratorEngagementItemsMap = Record<string, ConfiguratorEngagementItem>;

export interface ConfiguratorContextEngagementItem extends ConfiguratorEngagementItem {
  tiers: ConfiguratorContextEngagementTier[];
  active: boolean;
  onClick: ConfiguratorEngagementItemClickHandler;
  price: PricingCosts;
  showPrice: boolean;
}

export type ConfiguratorEngagementItemClickHandler = (nextItem: ConfiguratorEngagementItem) => void;

export interface ConfiguratorEngagementTier extends GamificationTier {}

export type ConfiguratorEngagementTiersMap = Record<string, ConfiguratorEngagementTier>;

export interface ConfiguratorContextEngagementTier extends ConfiguratorEngagementTier {
  active: boolean;
  onClick: ConfiguratorEngagementTierClickHandler;
  enabled: boolean;
  base_price?: number;
  discounted_price?: number;
}

export type ConfiguratorEngagementTierClickHandler = (
  nextItem: ConfiguratorEngagementItem,
  nextTier: ConfiguratorEngagementTier,
) => void;

export interface ConfiguratorEngagementClass extends GamificationClass {}

export interface ConfiguratorEngagementHighlight {
  title: TextTranslations;
  description: TextTranslations;
}

export interface ConfiguratorEngagementVideo extends ArtBundleProps {
  key: string; // `${itemId}_${tierId}`
  itemId: number;
  tierId: number;
  src: string;
  loop?: boolean;
}

export interface ConfiguratorEngagementCampaignItem extends CampaignItem {}

export type ConfiguratorEngagementCampaignItemsMap = Record<string, ConfiguratorEngagementCampaignItem[]>;

export interface ConfiguratorContextEngagementCampaignItem extends ConfiguratorEngagementCampaignItem {
  active: boolean;
  onClick: ConfiguratorContextEngagementCampaignItemClickHandler;
}

export type ConfiguratorContextEngagementCampaignItemClickHandler = (
  nextCampaign: ConfiguratorEngagementCampaignItem,
) => void;

export interface ConfiguratorEngagementCampaign extends Campaign {}

export type ConfiguratorEngagementCampaignsMap = Record<string, ConfiguratorEngagementCampaign[]>;

export interface ConfiguratorContextEngagementCampaign extends ConfiguratorEngagementCampaign {
  active: boolean;
  onClick: ConfiguratorContextEngagementCampaignClickHandler;
  items: ConfiguratorContextEngagementCampaignItem[];
  /** Price snapshot for this campaign (price_evaluation + isCustom marker). */
  offer: EngagementOffer;
  /** True for the synthetic Custom entry that mirrors the user's modified selection. */
  isCustom: boolean;
}

/** Per-feature breakdown for a single campaign — used to render the drawer's feature grid. */
export interface ConfiguratorEngagementCampaignFeature {
  /** Stable id for keying. */
  id: string;
  /** Localized feature/item name. */
  label: string;
  /** Icon URL pulled from the engagement item's art bundle (optional). */
  icon?: string;
  /** Localized tier name (e.g. 'Pro', 'Plus', 'Ultra'). */
  tierName: string;
  /** 1-based level of the active tier within the item's tier ladder. */
  level: number;
  /** Total levels available for this item (excluding 'off' tiers). */
  totalLevels: number;
  /** Progress percentage (level / totalLevels * 100), rounded. */
  progress: number;
  /** Market reference level (middle of the ladder). */
  marketLevel: number;
  /** Localized name of the market reference tier. */
  marketTierName: string;
  /** Market reference pin position, as a percentage. */
  marketProgress: number;
  /** 'ahead' | 'behind' | 'match' vs market reference. */
  comparison: 'ahead' | 'behind' | 'match';
}

export type ConfiguratorContextEngagementCampaignClickHandler = (nextCampaign: ConfiguratorEngagementCampaign) => void;

export interface ConfiguratorEngagementHtmlPreview extends ArtBundleProps {
  key: string; // `${itemId}_${tierId}`
  itemId: number;
  tierId: number;
  html: string;
}

// UI - Traffic
export interface ConfiguratorTrafficPlan extends EntryMeta {
  id: string;
  /** Resolved to the selected market's locale in buildTrafficPlans (not a raw translations map). */
  name: string;
}

export interface ConfiguratorContextTrafficPlan extends ConfiguratorTrafficPlan {
  active: boolean;
  onClick: ConfiguratorTrafficPlanClickHandler;
}

export type ConfiguratorTrafficPlanClickHandler = (nextPlan: ConfiguratorTrafficPlan) => void;

export interface ConfiguratorTrafficHighlightBullet {
  name: string;
  logo: string;
}

export interface ConfiguratorTrafficHighlightsGroup {
  title: string; // TextTranslations
  items: ConfiguratorTrafficHighlightBullet[];
  /** Count of additional sources beyond `items` (the "+N more" pill); 0 when none. */
  moreCount: number;
  /** Localized "+N more" label with {count} already interpolated; '' when none. */
  moreLabel: string;
}

export interface ConfiguratorTrafficHighlight {
  title: string; // TextTranslations
  description: string; // TextTranslations
  groups: ConfiguratorTrafficHighlightsGroup[];
}

// UI - Preview - Breakpoints
export interface ConfiguratorBreakpoint {
  id: string;
  name: TextTranslations;
  className: string;
}

export interface ConfiguratorContextBreakpoint extends ConfiguratorBreakpoint {
  active: boolean;
  onClick: ConfiguratorBreakpointClickHandler;
}

export type ConfiguratorBreakpointClickHandler = (nextBreakpoint: ConfiguratorBreakpoint) => void;

// UI - Context
export interface ConfiguratorContextInitValue {
  configError: string | undefined;
  configLoading: boolean;
  isMobile: boolean;
  isDesktop: boolean;
  price: PriceMap;
}

export interface ConfiguratorContextCountriesValue {
  country: Country | undefined;
  countries: ConfiguratorContextCountryGroup[];
  countryOpenSelectModal: boolean;
  onCountrySelectModalToggle: () => void;
  onConfirmCountry: () => void;
}

export interface ConfiguratorContextStepsValue {
  step: ConfiguratorStep;
  steps: ConfiguratorContextStep[];
  isFirstStep: boolean;
  isLastStep: boolean;
  onPreviousStepClick: () => void;
  onNextStepClick: () => void;
  progress: number;
  onCancelPaymentClick: () => void;
  /** False while a step-specific blocking phase is running (e.g. the market-scan animation on the Engagement step). */
  showNavigation: boolean;
}

export interface ConfiguratorContextDesignValue {
  designCategory: ConfiguratorDesignCategory;
  designCategories: ConfiguratorContextDesignCategory[];
  designThemeOption: ConfiguratorDesignThemeOption | undefined;
  designThemeOptions: ConfiguratorContextDesignThemeOption[];
  designThemeExpanded: boolean;
  onDesignThemeToggle: ConfiguratorDesignCategoryClickHandler;
  designColorsOption: ConfiguratorDesignColorsOption | undefined;
  designColorsOptions: ConfiguratorContextDesignColorsOption[];
  designColorsExpanded: boolean;
  onDesignColorsToggle: ConfiguratorDesignCategoryClickHandler;
  designArtOption: ConfiguratorDesignArtOption | undefined;
  designArtOptions: ConfiguratorDesignArtOption[];
  designArtExpanded: boolean;
  onDesignArtToggle: ConfiguratorDesignCategoryClickHandler;
  /** Next/previous sub-category within the Design step; undefined at the edges. */
  nextDesignCategory: ConfiguratorDesignCategory | undefined;
  previousDesignCategory: ConfiguratorDesignCategory | undefined;
  /** Sub-category cycling; returns true when a move happened. */
  onDesignCategoryNext: () => boolean;
  onDesignCategoryPrevious: () => boolean;
  resetDesignCategory: () => void;
  setDesignCategoryToLast: () => void;
}

export interface ConfiguratorContextEngagementValue {
  engagementCampaign: Campaign | undefined;
  engagementCampaigns: ConfiguratorContextEngagementCampaign[];
  engagementItem: ConfiguratorEngagementItem | undefined;
  engagementItems: ConfiguratorContextEngagementItem[];
  engagementHighlights: ConfiguratorEngagementHighlight[];
  engagementHighlightsHeader: ConfiguratorEngagementHighlight | undefined;
  engagementVideos: ConfiguratorEngagementVideo[];
  engagementSelectedVideo: ConfiguratorEngagementVideo | undefined;
  engagementHtmlPreviews: ConfiguratorEngagementHtmlPreview[];
  engagementSelectedHtml: ConfiguratorEngagementHtmlPreview | undefined;
  engagementOpenSelectModal: boolean;
  onEngagementSelectModalToggle: () => void;
  /** True once the user has confirmed a recommended campaign from market-scan. */
  hasRecommendedCampaign: boolean;
  /** True after the market-scan animation has played for the current country; false after country change. */
  marketScanPlayed: boolean;
  onMarketScanPlayed: () => void;
  /** Display name of the campaign selected in market-scan, undefined if none confirmed yet. */
  marketScanPackName: string | undefined;
  /** Campaign-level offer derived from `price_evaluation`; undefined when no campaign is active. */
  engagementOffer: EngagementOffer | undefined;
  /** True when the user has deviated from the campaign's `default_items`. */
  engagementIsCustomPack: boolean;
  /** id of the campaign whose details drawer is open (or undefined when closed). Independent of confirmation. */
  engagementDrawerCampaignId: string | undefined;
  /** Open the details drawer for a given campaign id (use 'custom' for the synthetic Custom entry). */
  onEngagementDrawerOpen: (campaignId: string) => void;
  /** Close the details drawer. */
  onEngagementDrawerClose: () => void;
  /** Campaign currently displayed in the drawer; undefined when closed or id unresolved. */
  engagementDrawerCampaign: ConfiguratorContextEngagementCampaign | undefined;
  /** Per-feature breakdown for the drawer's campaign — feature grid + market comparison. */
  engagementDrawerFeatures: ConfiguratorEngagementCampaignFeature[];
}

export interface ConfiguratorContextTrafficValue {
  trafficPlan: ConfiguratorTrafficPlan | undefined;
  trafficPlans: ConfiguratorContextTrafficPlan[];
  trafficHighlights: ConfiguratorTrafficHighlight[];
  /** Selected plan's center-stage video, shaped like engagementVideos for the [oh] preview list. */
  trafficVideos: ConfiguratorEngagementVideo[];
}

export interface ConfiguratorContextLiveValue {}

export interface ConfiguratorContextPaymentValue {
  paymentStatus: string | null;
}

export interface ConfiguratorContextFullscreenValue {
  onEnterFullscreen: () => void;
  onExitFullscreen: () => void;
  showEnterFullscreen: boolean;
  showExitFullscreen: boolean;
}

export interface ConfiguratorContextBreakpointsValue {
  breakpoint: ConfiguratorBreakpoint;
  breakpoints: ConfiguratorContextBreakpoint[];
  modalBreakpoint: ConfiguratorBreakpoint;
  modalBreakpoints: ConfiguratorContextBreakpoint[];
  openPreviewModal: boolean;
  onPreviewModalToggle: () => void;
}

export interface ConfiguratorContextIframeValue {
  iframeLoading: boolean;
  iframeUrl: string | undefined;
}

export type ConfiguratorContextValue = ConfiguratorContextInitValue &
  ConfiguratorContextCountriesValue &
  ConfiguratorContextStepsValue &
  ConfiguratorContextDesignValue &
  ConfiguratorContextEngagementValue &
  ConfiguratorContextTrafficValue &
  ConfiguratorContextLiveValue &
  ConfiguratorContextPaymentValue &
  ConfiguratorContextFullscreenValue &
  ConfiguratorContextBreakpointsValue &
  ConfiguratorContextIframeValue;

// Session Redux State
export enum CampaignItemType {
  BASE_ITEM = 'BASE_ITEM',
  GAMIFICATION_ITEM = 'GAMIFICATION_ITEM',
}

export interface ApplicabilityWindow {
  type: string;
  duration: string;
}

export interface DefaultItem {
  item_id: number;
  tier_id: string | number;
  type: CampaignItemType;
}

export interface FeatureRule {
  price_set_id: number;
  eligible_tiers: (string | number)[];
}

export interface Guardrails {
  feature_rules: FeatureRule[];
}

export interface DiscountArtBundle {
  text_name: TextTranslations;
  properties: Record<string, string>;
}

export interface DiscountKey {
  art_bundle: DiscountArtBundle[];
}

export interface CampaignItemTier {
  id: string | number;
  name: string;
  base_price: number;
  discounted_price?: number;
  discount_key?: DiscountKey;
  enabled: boolean;
}

export interface CampaignItem {
  id: number;
  priority: number;
  type: CampaignItemType;
  name: string;
  description?: string;
  tiers: CampaignItemTier[];
  required: boolean;
  recurrent: boolean;
  /** When false, the FEATURE is disabled for this campaign and must NOT contribute to the price
   *  (the backend's `price_evaluation` excludes it — mirror that in every FE price sum). */
  enabled?: boolean;
  price_set_id: number;
  feature_id: number;
}

export interface PriceEvaluation {
  match: boolean;
  /** Whole-campaign base total (setup + features). */
  basePrice: number;
  /** Whole-campaign discounted total (0 when `match` is false → no discount, use `basePrice`). */
  discountedPrice: number;
  /** Backend-differentiated SETUP fee (base), authoritative — prefer over summing FE-side. */
  setupPrice?: number;
  /** Backend-differentiated setup fee (discounted). */
  discountedSetupPrice?: number;
  /** Backend-differentiated FEATURES total (base), authoritative — prefer over summing FE-side. */
  featuresPrice?: number;
  /** Backend-differentiated features total (discounted). */
  discountedFeaturesPrice?: number;
}

/**
 * A campaign's art-bundle entry. Carries the human DISPLAY name (localized `text_name`, else
 * `properties.name`) — the backend moved the pack's shown name (Basic / Focused / Edge / Superstar)
 * here while the campaign `name` holds an internal id. Loosely typed (the backend ships a large,
 * mostly-unused shape); we only read the name.
 */
export interface CampaignArtBundle {
  text_name?: TextTranslations;
  properties?: {
    section_id?: string;
    /** The pack's localized DISPLAY name — the field the backend populates for campaigns. */
    display_name?: TextTranslations;
    /** The pack's localized blurb (shown under the name on the card). */
    display_description?: TextTranslations;
    name?: TextTranslations;
  } & Record<string, unknown>;
}

export interface Campaign {
  applicability_window: ApplicabilityWindow;
  availability_window: EntryTimeDuration;
  currency_code: string;
  default_config: Record<string, string | number>;
  default_items: DefaultItem[];
  guardrails: Guardrails;
  id: string;
  items: CampaignItem[];
  /** Internal campaign id/name (e.g. "MX low-pricing … EDGE"). The human display name lives on
   *  {@link art_bundle} — render via `campaignDisplayName`, not this. */
  name: string;
  /** Presentation art bundle — source of the pack's localized DISPLAY name. */
  art_bundle?: CampaignArtBundle[];
  /** Optional localized blurb shown on each package card; backend may omit it. */
  description?: TextTranslations;
  price_evaluation: PriceEvaluation;
}

export interface ConfiguratorSessionStateRoot {
  // Internal Parameters
  lastStep?: string;
  stepsVisited?: Record<string, boolean>;
  previewUrl?: string;
  // Actual Data Parameters
  countryCode?: string;
  /** ISO 4217 code the session is PRICED in (e.g. `USD` / `MXN`). Set at mint from
   *  `startProperties.currency` and changed via `/session/set-currency`; the backend re-prices
   *  the bound + recommended campaigns to it. Independent of `countryCode` (the operating region). */
  currency?: string;
  emailAddress?: string;
  /** Marketing-site voucher / promo code applied to the session (e.g. an affiliate code). The
   *  backend persists it here and evaluates it, echoing the applied discount on the session's
   *  top-level `voucher` object. `null` when no voucher is applied. */
  voucher_code?: string | null;
}

export interface ConfiguratorSessionDesignState {
  siteSeedId?: number;
  siteSkinId?: number;
  artStyleId?: number;
  tokens?: Tokens;
}

export interface ConfiguratorSessionEngagementState {
  campaignId?: string;
  items?: DefaultItem[];
}

export interface ConfiguratorSessionTrafficState {
  /** Selected step-3 traffic/audience option (PlayerOption.id, e.g. "traffic_option_sports"). */
  traffic_option_id?: string;
}

/**
 * What the CHECKOUT writes back onto the session: which payment rail the buyer chose, and one
 * funnel accumulator per rail.
 *
 * Written only by the Mexican flows (`v0-mx` / `v3-mx`), which are the ones offering a choice of
 * rail, but declared on the shared session shape because that is what `/session/save` round-trips.
 * The accumulators' merge rules (and their tests) live in
 * [`@/utils/session-checkout-accounting`](../../../../utils/session-checkout-accounting.ts).
 */
export interface ConfiguratorSessionCheckoutState {
  /** The rail the buyer last selected, as the `provider` slug `/session/invoice` takes. Lets a
   *  reopened checkout come back on the method they were already committed to. */
  payment_method?: CheckoutProvider;
  /** SPEI (bank transfer) funnel: sub-steps reached, payer name, coordinates copied. */
  spei_checkout?: SpeiCheckoutAccounting;
  /** Card funnel: attempts, whether details were entered, and Stripe's own failure reasons. */
  stripe_checkout?: StripeCheckoutAccounting;
}

export type ConfiguratorSessionState = ConfiguratorSessionStateRoot &
  ConfiguratorSessionDesignState &
  ConfiguratorSessionEngagementState &
  ConfiguratorSessionTrafficState &
  ConfiguratorSessionCheckoutState;

export type SetSessionCallback = (data: Partial<ConfiguratorSessionState>) => void;
